import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Tooltip } from 'antd';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getStudentCourseBatches } from 'actions/studentManagement';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import PageHeader from 'components/pageHeader';
import PageDataLoader from 'components/pageDataLoader';
import illNoData from '../../Assets/ill-no-data.svg';
import userIconSvg from './dashboardV3/user-rounded12.svg';
import StudentListTable from 'components/UserDashboardV2/components/StudentListTable';
import AddBatchDrawer from 'components/UserDashboardV2/components/AddBatchDrawer';
import AddSectionDrawer from 'components/UserDashboardV2/components/AddSectionDrawer';
import UnassignedStudentsTable from 'components/UserDashboardV2/components/UnassignedStudentsTable';
import AddStudentModal from 'components/UserDashboardV2/components/AddStudentModal';

const TitleDivider = () => (
    <span style={{ display: 'inline-block', width: '2px', height: fluid(24), backgroundColor: '#191919', margin: `0 ${fluid(4)}`, flexShrink: 0 }} />
);

const DAY_NAMES = { 1: 'Sun', 2: 'Mon', 3: 'Tue', 4: 'Wed', 5: 'Thu', 6: 'Fri', 7: 'Sat' };

const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
};

const formatDays = (days) => {
    if (!Array.isArray(days) || days.length === 0) return 'Mon-Fri';
    const sorted = [...days].sort((a, b) => a - b);
    if (sorted.length === 5 && sorted[0] === 2 && sorted[4] === 6) return 'Mon-Fri';
    if (sorted.length === 6 && sorted[0] === 2 && sorted[5] === 7) return 'Mon-Sat';
    if (sorted.length === 7) return 'Mon-Sun';
    return sorted.map(d => DAY_NAMES[d] || d).join(', ');
};

const formatStartDate = (rawDate) => {
    if (!rawDate) return '';
    const parsed = new Date(rawDate);
    if (!isNaN(parsed.getTime())) {
        const formatted = new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).format(parsed);
        return `Started on ${formatted}`;
    }
    if (typeof rawDate === 'string' && rawDate.toLowerCase().startsWith('started on')) {
        return rawDate;
    }
    return `Started on ${rawDate}`;
};

const BatchCard = ({ batch, groupTitle, history, location }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
        <div
            onClick={() => history.push(`${location.pathname}/student-list`, {
                batchName: groupTitle,
                sectionName: batch.name,
                standardId: batch.id,
                courseId: location.state?.courseId,
                courseName: location.state?.courseName || location.state?.course?.courseName,
                courseType: (location.state?.courseType === 'live' ? 'course' : (location.state?.courseType || location.state?.resourceType || 'course')),
                course: location.state?.course,
            })}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                width: fluid(486),
                height: fluid(202),
                backgroundColor: '#FFFFFF',
                borderRadius: fluid(20),
                padding: `${fluid(20)} ${fluid(24)}`,
                boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 4px 10px rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.2s ease',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: 'pointer',
            }}
        >
            {/* Top row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: fluid(14), fontWeight: 400, color: '#636363', fontFamily: 'Roboto, sans-serif' }}>
                    {batch.schedule}
                </span>
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ECFDF5',
                    border: '1px solid #34D399',
                    borderRadius: fluid(4),
                    padding: `${fluid(3)} ${fluid(10)}`,
                    fontSize: fluid(12),
                    fontWeight: 600,
                    color: '#059669',
                    fontFamily: 'Figtree, Roboto, sans-serif',
                    height: fluid(22),
                    boxSizing: 'border-box'
                }}>
                    Active
                </div>
            </div>

            {/* Middle row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: fluid(16) }}>
                <div style={{
                    width: fluid(64),
                    height: fluid(64),
                    borderRadius: '50%',
                    backgroundColor: batch.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: fluid(20),
                    fontWeight: 500,
                    color: '#000000',
                    fontFamily: 'Roboto, sans-serif',
                    flexShrink: 0
                }}>
                    {batch.initials}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: fluid(4), minWidth: 0 }}>
                    <span style={{ fontSize: fluid(20), fontWeight: 500, color: '#000000', fontFamily: 'Roboto, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {batch.name}
                    </span>
                    <span style={{ fontSize: fluid(14), fontWeight: 400, color: '#636363', fontFamily: 'Roboto, sans-serif' }}>
                        {batch.date}
                    </span>
                </div>
            </div>

            {/* Bottom row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: fluid(8) }}>
                    <img src={userIconSvg} alt="" style={{ width: fluid(20), height: fluid(20) }} />
                    <span style={{ fontSize: fluid(14), fontWeight: 400, color: '#888888', fontFamily: 'Roboto, sans-serif' }}>
                        {batch.students} {Number(batch.students) === 1 ? 'student' : 'students'}
                    </span>
                </div>
            </div>
        </div>
    );
};

const UnassignedCard = ({ history, location, studentsCount }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
        <div
            onClick={() => history.push(`${location.pathname}/student-list`, {
                batchName: 'Unassigned Students',
                courseId: location.state?.courseId,
                courseName: location.state?.courseName || location.state?.course?.courseName,
                courseType: 'course',
                hasBatch: false,
            })}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                width: fluid(486),
                height: fluid(202),
                backgroundColor: '#FFFFFF',
                borderRadius: fluid(20),
                padding: `${fluid(20)} ${fluid(24)}`,
                boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 4px 10px rgba(0,0,0,0.04)',
                border: '1px solid #E6E6E6',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                gap: fluid(16)
            }}
        >
            <div style={{
                width: fluid(64),
                height: fluid(64),
                borderRadius: '50%',
                backgroundColor: '#FFF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <img src={userIconSvg} alt="" style={{ width: fluid(32), height: fluid(32) }} />
            </div>

            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: fluid(8) }}>
                <span style={{ fontSize: fluid(20), fontWeight: 500, color: '#191919', fontFamily: 'Roboto, sans-serif' }}>
                    Unassigned Students
                </span>
                <span style={{ fontSize: fluid(14), fontWeight: 400, color: '#DC2626', fontFamily: 'Roboto, sans-serif' }}>
                    {studentsCount} students need batch assignment
                </span>
            </div>
        </div>
    );
};

const StudentCourseBatches = ({ getStudentCourseBatches }) => {
    const history = useHistory();
    const location = useLocation();

    const [isAddBatchVisible, setIsAddBatchVisible] = useState(false);
    const [isAddSectionVisible, setIsAddSectionVisible] = useState(false);
    const [activeSectionBatchName, setActiveSectionBatchName] = useState("");
    const [standardsData, setStandardsData] = useState([]);
    const [unassignedCount, setUnassignedCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
    const [studentTableKey, setStudentTableKey] = useState(0);

    const isStudentList = location.pathname.endsWith('/student-list');
    const activeBatchName = location.state?.batchName;
    const hasBatchContext = !!activeBatchName;

    // Course information passed from course cards view
    const rawCourseId = location.state?.courseId;
    const courseId = (!rawCourseId || rawCourseId === 0 || rawCourseId === '0') ? -1 : rawCourseId;
    const courseName = location.state?.courseName || location.state?.course?.courseName || 'Course Batches';

    const fetchBatches = useCallback(() => {
        setIsLoading(true);
        getStudentCourseBatches?.(courseId, (response, error) => {
            setIsLoading(false);
            if (response && !error) {
                const data = response?.data || response;
                const stdArray = data?.standardsArray ?? (Array.isArray(data) ? data : []);
                setStandardsData(stdArray);

                const count = data?.unassigned_student_with_course
                    ?? data?.std_wo_course_student
                    ?? data?.unassignedStudentCount
                    ?? 0;
                setUnassignedCount(Number(count) || 0);
            }
        });
    }, [courseId, getStudentCourseBatches]);

    useEffect(() => {
        fetchBatches();
    }, [fetchBatches]);

    const colorList = ['#D8F0FF', '#DCF2B7', '#FFEFC5', '#FFDEDF'];

    let totalActiveBatchesCount = 0;
    let totalStudentsCount = 0;

    const groupedBatches = (standardsData && standardsData.length > 0)
        ? standardsData.map((group, groupIdx) => {
            const rawStd = String(group.std ?? group.name ?? group.title ?? '').trim();
            const isValidStd = rawStd !== '' && rawStd !== '0';
            const groupTitle = isValidStd ? (rawStd.charAt(0).toUpperCase() + rawStd.slice(1)) : '';
            const items = group.standards || group.batches || group.sections || [];
            const batchCards = items.map((b, idx) => {
                const sec = b.section !== undefined && b.section !== null ? String(b.section).trim() : '';
                // Show the section name exactly as the API sends it — no "Section" prefix.
                const name = sec || b.name || b.batchName || b.standardName || `${idx + 1}`;
                // Avatar shows the section name itself (no "S" prefix); names too long
                // for the circle (over 3 characters) show just their first letter.
                const initials = name.length <= 3 ? name : name.charAt(0).toUpperCase();
                const students = b.studentCount ?? b.students ?? b.count ?? 0;
                let schedule = 'Mon-Fri | 10:30-11:30 AM';
                if (b.startTime && b.endTime) {
                    schedule = `${formatDays(b.days)} | ${formatTime(b.startTime)} - ${formatTime(b.endTime)}`;
                } else if (b.schedule || b.timing) {
                    schedule = b.schedule || b.timing;
                }
                const rawDate = b.startDate || b.date || b.createdAt;
                let date = '';
                if (rawDate) {
                    date = formatStartDate(rawDate);
                } else if (b.courseName) {
                    date = `Course: ${b.courseName}`;
                } else {
                    date = 'Started on 12 Aug 2026';
                }
                const color = b.color || colorList[idx % colorList.length];
                totalActiveBatchesCount += 1;
                totalStudentsCount += Number(students);
                return {
                    id: b.standard_id || b.id || b._id || `${groupIdx}-${idx}`,
                    initials,
                    name,
                    date,
                    students,
                    schedule,
                    color,
                };
            });
            return {
                title: groupTitle,
                std: group.std || group.name || group.title || rawStd,
                batches: batchCards,
            };
        })
        : [];

    const unassignedStudentsCount = unassignedCount;

    // Render Drill-down Students List view (Screen 2)
    if (isStudentList) {
        const displayCourseName = location.state?.courseName || location.state?.course?.courseName || courseName;
        const displayBatchName = location.state?.batchName;
        const displaySectionName = location.state?.sectionName;

        const isValidTitle = (val) => {
            if (val === null || val === undefined) return false;
            const str = String(val).trim();
            return str !== '' && str !== '0';
        };

        const headerParts = [];
        if (isValidTitle(displayCourseName)) headerParts.push(displayCourseName);
        if (isValidTitle(displayBatchName) && displayBatchName !== displayCourseName && displayBatchName !== displaySectionName) {
            headerParts.push(displayBatchName);
        }
        if (isValidTitle(displaySectionName) && displaySectionName !== displayCourseName) {
            headerParts.push(displaySectionName);
        }
        if (headerParts.length === 0 && isValidTitle(activeBatchName)) {
            headerParts.push(activeBatchName);
        }

        return (
            <div style={{
                backgroundColor: 'transparent',
                height: '100%',
                width: 'calc(100% + 30px)',
                margin: -20,
                padding: '16px 20px 0px 24px',
                boxSizing: 'border-box',
                fontFamily: 'Roboto, sans-serif',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                marginTop: 0,
            }}>
                {/* Header (Sticky) */}
                <PageHeader
                    title={
                        <span style={{ display: 'flex', alignItems: 'center', gap: fluid(8) }}>
                            {headerParts.map((part, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && <TitleDivider />}
                                    <span>{part}</span>
                                </React.Fragment>
                            ))}
                        </span>
                    }
                    onBackPress={() => history.goBack()}
                />

                {/* Student list content */}
                <div style={{ margin: '30px 0 0 0', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '0px' }} className="dv2-chart-scrollbar">
                        {activeBatchName === 'Unassigned Students' ? (
                            <UnassignedStudentsTable
                                isMobile={false}
                                courseId={location.state?.courseId}
                                courseType={(location.state?.courseType === 'live' ? 'course' : location.state?.courseType) || 'course'}
                                hasBatch={false}
                                courseName={displayCourseName}
                                onBatchAssigned={fetchBatches}
                            />
                        ) : (
                            <StudentListTable
                                key={studentTableKey}
                                isMobile={false}
                                standardId={location.state?.standardId}
                                onAddStudent={() => setIsAddStudentModalOpen(true)}
                            />
                        )}
                    </div>
                </div>

                <AddStudentModal
                    visible={isAddStudentModalOpen}
                    onClose={() => setIsAddStudentModalOpen(false)}
                    courseId={location.state?.courseId}
                    courseName={displayCourseName}
                    courseType={(location.state?.courseType === 'live' ? 'course' : location.state?.courseType) || location.state?.resourceType || 'course'}
                    standardId={location.state?.standardId}
                    batchName={location.state?.batchName}
                    sectionName={location.state?.sectionName}
                    onSuccess={() => {
                        setStudentTableKey(prev => prev + 1);
                        fetchBatches();
                    }}
                />
            </div>
        );
    }

    const hasContent = unassignedStudentsCount > 0 || groupedBatches.some(g => g.batches.length > 0);

    return (
        <div style={{
            backgroundColor: 'transparent',
            height: '100%',
            width: 'calc(100% + 30px)',
            margin: -20,
            padding: '16px 20px 0px 24px',
            boxSizing: 'border-box',
            fontFamily: 'Roboto, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            marginTop: 0,
            position: 'relative',
        }}>
            <PageDataLoader visible={isLoading} />
            {/* Header + Actions (Sticky) */}
            <PageHeader
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: fluid(8) }}>
                        {/* Long course names are cut off with "..." (full name on hover)
                            so the subtitle and ADD A NEW BATCH stay visible. */}
                        <Tooltip title={`Batches in ${courseName}`}>
                            <span style={{
                                display: 'inline-block',
                                maxWidth: fluid(600),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                Batches in {courseName}
                            </span>
                        </Tooltip>
                        <TitleDivider />
                        <span style={{ fontSize: fluid(18), fontWeight: 400, color: '#636363', fontFamily: 'Roboto, sans-serif', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            Classroom course &middot; {totalActiveBatchesCount} active batches &middot; {totalStudentsCount} students total
                        </span>
                    </span>
                }
                onBackPress={() => history.goBack()}
                actions={[
                    <button
                        type="button"
                        onClick={() => setIsAddBatchVisible(true)}
                        style={{
                            width: fluid(205),
                            height: fluid(45),
                            borderRadius: fluid(12),
                            backgroundColor: '#594099',
                            color: '#FFFFFF',
                            border: 'none',
                            fontSize: fluid(16),
                            fontWeight: 700,
                            fontFamily: 'Roboto, sans-serif',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        ADD A NEW BATCH
                    </button>
                ]}
            />

            {/* Scrollable Batch List area */}
            <div style={{
                margin: '30px -20px 0 -20px',
                padding: '10px 20px 0 20px',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingBottom: fluid(30)
            }} className="dv2-chart-scrollbar">

                {/* Unassigned Students Section — hidden for now (kept commented, not removed). */}
                {/* {unassignedStudentsCount > 0 && (
                    <div style={{ marginBottom: fluid(40) }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: fluid(20) }}>
                            <span style={{ fontSize: fluid(24), fontWeight: 500, color: '#191919' }}>
                                Unassigned Students
                            </span>
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(auto-fill, ${fluid(486)})`,
                            gap: fluid(24),
                            width: '100%'
                        }}>
                            <UnassignedCard history={history} location={location} studentsCount={unassignedStudentsCount} />
                        </div>
                    </div>
                )} */}

                {/* Empty State */}
                {!hasContent && !isLoading && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        minHeight: fluid(350),
                        width: '100%',
                    }}>
                        <img src={illNoData} style={{ width: fluid(250) }} alt="No batches found" />
                        <span style={{ fontSize: fluid(18), fontWeight: 500, color: '#636363', marginTop: fluid(16), fontFamily: 'Roboto, sans-serif' }}>
                            No batches found in this course
                        </span>
                    </div>
                )}

                {/* Dynamic Batch Sections */}
                {groupedBatches.map((group, gIdx) => (
                    <div key={gIdx} style={{ marginBottom: fluid(40) }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: fluid(20) }}>
                            <span style={{ fontSize: fluid(24), fontWeight: 500, color: '#191919' }}>
                                {group.title}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setActiveSectionBatchName(group.std || group.title);
                                    setIsAddSectionVisible(true);
                                }}
                                style={{
                                    width: fluid(219),
                                    height: fluid(45),
                                    borderRadius: fluid(12),
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#594099',
                                    fontSize: fluid(16),
                                    fontWeight: 700,
                                    fontFamily: 'Roboto, sans-serif',
                                    cursor: 'pointer',
                                    textAlign: 'right'
                                }}
                            >
                                ADD A NEW SECTION
                            </button>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(auto-fill, ${fluid(486)})`,
                            gap: fluid(24),
                            width: '100%'
                        }}>
                            {group.batches.map(batch => <BatchCard key={batch.id} batch={batch} groupTitle={group.title} history={history} location={location} />)}
                        </div>
                    </div>
                ))}
            </div>

            <AddBatchDrawer
                visible={isAddBatchVisible}
                onClose={() => setIsAddBatchVisible(false)}
                courseName={courseName}
                courseId={location.state?.courseId}
                courseType={location.state?.courseType || 'course'}
                onSuccess={() => {
                    setIsAddBatchVisible(false);
                    fetchBatches();
                }}
            />

            <AddSectionDrawer
                visible={isAddSectionVisible}
                onClose={() => setIsAddSectionVisible(false)}
                batchName={activeSectionBatchName}
                std={activeSectionBatchName}
                courseId={location.state?.courseId}
                onSuccess={() => {
                    setIsAddSectionVisible(false);
                    fetchBatches();
                }}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = (dispatch) => ({
    getStudentCourseBatches: (courseId, callback) => dispatch(getStudentCourseBatches(courseId, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StudentCourseBatches);
